import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { CreateRolloverPlanRequest, RolloverActionRow, RolloverDecision } from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Session rollover.
 * List of plans; each plan is a preview of every student's next-year placement.
 * Commit is atomic (applies grade/section/status to every student). Reversal
 * flips back until fees or marks accrue against the new session (window
 * enforcement lands next iteration).
 */
export function RolloverPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['rollover', institution],
    queryFn: () => productApi.listRollover(institution),
    enabled: !!institution,
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Session rollover</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Draft → review each student's next-year placement → commit atomically. Reversal supported until real writes accrue against the new session (S-03).
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          New rollover
        </Button>
      </div>

      <Card size="small" title={`${list.data?.total ?? 0} plans`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {list.data && list.data.rows.length === 0 && (
          <Empty description={<span>No rollover plans yet. <a onClick={() => setCreateOpen(true)}>Draft the first one</a>.</span>} style={{ padding: 20 }} />
        )}
        {list.data && list.data.rows.length > 0 && (
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={list.data.rows}
            onRow={(r) => ({ onClick: () => setDetailId(r.id), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'From', dataIndex: 'from_label', key: 'f', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span> },
              { title: 'To', dataIndex: 'to_label', key: 't', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span> },
              { title: 'Actions', dataIndex: 'action_count', key: 'a', width: 100, align: 'right' },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 's',
                width: 120,
                render: (v: string) => <Tag color={v === 'committed' ? 'success' : v === 'reversed' ? 'default' : 'processing'}>{v}</Tag>,
              },
              {
                title: 'Committed',
                dataIndex: 'committed_at',
                key: 'c',
                width: 170,
                render: (v: string | null) => v ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : <Typography.Text type="secondary">—</Typography.Text>,
              },
            ]}
          />
        )}
      </Card>

      <CreatePlanModal
        open={createOpen}
        institution={institution}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setCreateOpen(false);
          setDetailId(id);
          void message.success('Draft created');
        }}
      />
      <PlanDetailModal
        planId={detailId}
        institution={institution}
        onClose={() => setDetailId(null)}
      />
    </Space>
  );
}

function CreatePlanModal({
  open, institution, onClose, onCreated,
}: { open: boolean; institution: string; onClose: () => void; onCreated: (id: string) => void }) {
  const [form] = Form.useForm<{ from_session_id: string; to_session_id: string; progression: string }>();

  const cal = useQuery({
    queryKey: ['calendar', institution],
    queryFn: () => productApi.getCalendar(institution),
    enabled: !!institution && open,
  });

  const students = useQuery({
    queryKey: ['students', institution, {}],
    queryFn: () => productApi.listStudents(institution),
    enabled: !!institution && open,
  });
  const distinctGrades = useMemo(() => {
    const set = new Set<string>();
    for (const s of students.data?.rows ?? []) set.add(s.grade);
    return Array.from(set).sort();
  }, [students.data]);
  const defaultProgression = useMemo(() =>
    distinctGrades.map((g) => `"${g}": "${nextGuess(g)}"`).join(',\n  '),
    [distinctGrades],
  );

  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: CreateRolloverPlanRequest) => productApi.createRollover(institution, input),
    onSuccess: (r) => {
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['rollover', institution] });
      onCreated(r.id);
    },
    onError: (e) => void message.error(errMsg(e) ?? 'Create failed'),
  });

  return (
    <Modal
      open={open}
      title="New rollover"
      onCancel={() => { m.reset(); onClose(); }}
      okText="Draft plan"
      confirmLoading={m.isPending}
      onOk={() => form.submit()}
      destroyOnClose
      width={640}
    >
      {!cal.data || cal.data.sessions.length < 2 ? (
        <Alert type="warning" showIcon message="Need at least two sessions (from + to). Add one in Setup step 2." />
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={{ progression: `{\n  ${defaultProgression}\n}` }}
          onFinish={(v) => {
            let progression: Record<string, string>;
            try {
              progression = JSON.parse(v.progression);
            } catch {
              void message.error('Progression must be valid JSON.');
              return;
            }
            m.mutate({
              from_session_id: v.from_session_id,
              to_session_id: v.to_session_id,
              progression,
            });
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="from_session_id" label="From session" rules={[{ required: true }]}>
              <Select options={(cal.data?.sessions ?? []).map((s) => ({ value: s.id, label: s.label }))} />
            </Form.Item>
            <Form.Item name="to_session_id" label="To session" rules={[{ required: true }]}>
              <Select options={(cal.data?.sessions ?? []).map((s) => ({ value: s.id, label: s.label }))} />
            </Form.Item>
          </div>
          <Form.Item
            name="progression"
            label="Progression map (JSON)"
            tooltip="Map from-grade → to-grade. Use 'graduate' to graduate the class."
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={7} style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }} />
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>
            Students in a grade not in the map default to <b>detain</b> — you can override per student in the plan detail.
          </Typography.Text>
        </Form>
      )}
    </Modal>
  );
}

function PlanDetailModal({
  planId, institution, onClose,
}: { planId: string | null; institution: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['rollover', institution, planId],
    queryFn: () => productApi.getRollover(institution, planId!),
    enabled: !!institution && !!planId,
  });

  const commit = useMutation({
    mutationFn: () => productApi.commitRollover(institution, planId!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rollover', institution] });
      void qc.invalidateQueries({ queryKey: ['students', institution] });
      void qc.invalidateQueries({ queryKey: ['briefing', institution] });
      void message.success('Committed');
    },
    onError: (e) => void message.error(errMsg(e) ?? 'Commit failed'),
  });
  const reverse = useMutation({
    mutationFn: () => productApi.reverseRollover(institution, planId!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rollover', institution] });
      void qc.invalidateQueries({ queryKey: ['students', institution] });
      void message.success('Reversed');
    },
    onError: (e) => void message.error(errMsg(e) ?? 'Reverse failed'),
  });

  const plan = q.data;

  return (
    <Modal
      open={!!planId}
      onCancel={onClose}
      title={plan ? `Rollover · ${plan.from_label} → ${plan.to_label}` : 'Loading…'}
      width={900}
      footer={
        plan ? (
          <Space>
            <Button onClick={onClose}>Close</Button>
            {plan.status === 'draft' && (
              <Popconfirm
                title="Commit this rollover?"
                description="Applies every action atomically. Reversible until real writes accrue against the new session."
                onConfirm={() => commit.mutate()}
                okText="Commit"
                okButtonProps={{ danger: true }}
              >
                <Button type="primary" loading={commit.isPending}>Commit</Button>
              </Popconfirm>
            )}
            {plan.status === 'committed' && (
              <Popconfirm
                title="Reverse this rollover?"
                description="Puts every student back to their previous grade/section. Log entry preserved."
                onConfirm={() => reverse.mutate()}
                okText="Reverse"
                okButtonProps={{ danger: true }}
              >
                <Button danger loading={reverse.isPending}>Reverse</Button>
              </Popconfirm>
            )}
          </Space>
        ) : null
      }
    >
      {q.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
      {plan && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 12, marginBottom: 12 }}>
            <Card size="small"><Statistic title="Total" value={plan.summary.total} /></Card>
            <Card size="small"><Statistic title="Promote" value={plan.summary.promote} valueStyle={{ color: '#1c6442' }} /></Card>
            <Card size="small"><Statistic title="Detain" value={plan.summary.detain} valueStyle={{ color: plan.summary.detain > 0 ? '#a35800' : undefined }} /></Card>
            <Card size="small"><Statistic title="Graduate" value={plan.summary.graduate} /></Card>
            <Card size="small"><Statistic title="Transfer out" value={plan.summary.transfer_out} /></Card>
          </div>
          <Table<RolloverActionRow>
            size="small"
            pagination={{ pageSize: 12 }}
            rowKey="id"
            dataSource={plan.actions}
            columns={[
              { title: 'Adm. no.', dataIndex: 'admission_number', key: 'a', width: 110, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Name', dataIndex: 'student_name', key: 'n' },
              { title: 'From', key: 'f', width: 90, render: (_, r) => `${r.from_grade}${r.from_section ? `-${r.from_section}` : ''}` },
              { title: 'To', key: 't', width: 100, render: (_, r) => r.to_grade ? `${r.to_grade}${r.to_section ? `-${r.to_section}` : ''}` : <Typography.Text type="secondary">—</Typography.Text> },
              {
                title: 'Decision',
                dataIndex: 'decision',
                key: 'd',
                width: 110,
                render: (v: RolloverDecision) => <Tag color={v === 'promote' ? 'success' : v === 'graduate' ? 'processing' : v === 'detain' ? 'warning' : 'default'}>{v.replace(/_/g, ' ')}</Tag>,
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
}

function nextGuess(grade: string): string {
  const n = Number(grade);
  if (!Number.isNaN(n)) {
    if (n >= 12) return 'graduate';
    return String(n + 1);
  }
  return grade;
}

function errMsg(err: unknown): string | null {
  if (!err) return null;
  const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
  return body?.message ?? (err instanceof Error ? err.message : null);
}
